import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useMutation, gql } from '@apollo/client';
import Loader from 'react-loader-spinner';
import { Checkmark } from 'react-checkmark';

const TransitionStateWrapper = styled.div`
  transform: translateY(90px);
`;

const FormInput = styled.input`
  margin-top: 5px;
  display: block;
  padding: 1px 2px;
  border: none;
  border-bottom: rgba(189,189,189) 1px solid;
  font-family: 'Questrial', sans-serif;
  outline: none;
  margin-left: 25px;
  width: 80%;
  margin-bottom: 20px;
  height: 30px;
`;

const FormComment = styled.textarea`
  display: block;
  padding: 0px 2px;
  margin-top: 10px;
  border: none;
  border-bottom: rgba(189,189,189) 1px solid;
  margin-left: 25px;
  width: 80%;
  outline: none;
  margin-bottom: 20px;
  font-family: 'Questrial', sans-serif;
  height: 30px;
  resize: none;
  height: 50px;
  &::placeholder {
    line-height: 50px;
  }
  &:focus::placeholder {
    color: transparent;
  }
`;

const FormLabel = styled.label`
  margin-left: 20px;
  font-size: 14px;
  font-weight: bold;
  color: rgba(189,189,189);
`;

const FormHeading = styled.div`
  font-family: 'Questrial', sans-serif;
  color: white;
  font-size: 14px;
  margin-bottom: 10px;
  margin-top: 17px;
  font-size: 15px;
`;

const Form = styled.form`
  margin-top: 20px;
  width: 100%;
`;

const FormButton = styled.button`
  display: block;
  background-color: #dd8a5f;
  color: white;
  border-radius: 10px;
  font-family: 'Questrial', sans-serif;
  margin-top: 5px;
  border: none;
  font-size: 14px;
  padding: 15px;
  margin-left: 50%;
  position: relative;
  width: 100px;
  transform: translateX(-50px);
  outline: none;
  transition: all 300ms ease;

  &:hover {
    transform: translateY(-2px) translateX(-50px);
    box-shadow: 0 12px 24px rgba(0,0,0,.2);
  }

  &:active {
    transform: translateY(-1px) translateX(-50px);
    box-shadow: 0 6px 12px rgba(0,0,0,.2);
  }
`;

const SuccessMessage = styled.div`
  text-align: center;
  font-size: 16px;
  font-weight: bold;
  color: #dd8a5f;
  margin-top: 40px;
  animation: fadeIn 500ms ease;
  animation-delay: 1s;
  opacity: 0;
  animation-fill-mode: forwards;

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }
`;

const ErrorMessage = styled.div`
  color: rgb(214, 70, 60,0.8);
  text-align: center;
  margin-top: 40%;
  font-size: 24px;
`;

const SuccessMessagePrimary = styled.div`
  margin-bottom: 35px;
  font-size: 28px;
`;

const SuccessMessageSecondary = styled.div`
  font-size: 18px;
`;

const FormBodyWrapper = styled.div`
  background-color: white;
  height: 350px;
  border-radius: 20px;
  border: white solid 1px;
  box-shadow: -2px 2px 2px white;
`;

const FormWrapper = styled.div`
  margin-top: 20px;
`;

const CREATE_COMMENT = gql`
mutation CreateComment(
  $email: String
  $comment: String
  $id: Int
  $name: String
) {
  __typename
  createComment(
    input: {
      clientMutationId: "createComment"
      authorEmail: $email
      commentOn: $id
      content: $comment
      author: $name
    }
  ) {
    success
  }
}
`;

const handleInputChange = (event, setState) => {
  setState(event.target.value);
};

const stopFullscreenShortcut = (e) => {
  if (e.key === 'f') {
    e.nativeEvent.stopImmediatePropagation(); // disable modal shortcut for fullscreening
  }
};

const CommentForm = ({ views, currentIndex }) => {
  const [userEmail, setUserEmail] = useState();
  const [userComment, setUserComment] = useState();
  const [userName, setUserName] = useState();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [addComment, { data, loading, error }] = useMutation(CREATE_COMMENT);

  const { id } = views[currentIndex];

  useEffect(() => {
    setUserComment('');
  }, [currentIndex, showSuccess]);

  useEffect(() => {
    setShowSuccess(false);
    setShowError(false);
    setShowLoading(false);
  }, [currentIndex]);
  let commentBox;
  commentBox = (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        setShowLoading(true);
        addComment({
          variables: {
            email: userEmail,
            comment: userComment,
            id,
            name: userName,
          },
          onCompleted: setTimeout(() => {
            setShowLoading(false);
            setShowSuccess(true);
          }, 850),
          onError: setShowError(true),
        });
      }}
    >
      <FormLabel>
        Name
        <FormInput
          required
          placeholder="First Name"
          type="text"
          name="name"
          value={userName}
          onChange={(e) => handleInputChange(e, setUserName)}
          onKeyUp={stopFullscreenShortcut}
        />
      </FormLabel>
      <FormLabel>
        Email
        <FormInput
          required
          placeholder="example@email.com"
          type="email"
          name="email"
          value={userEmail}
          onChange={(e) => handleInputChange(e, setUserEmail)}
          onKeyUp={stopFullscreenShortcut}
        />
      </FormLabel>
      <FormLabel>
        Comment
        <FormComment
          required
          placeholder="What would you like to tell us?"
          type="text"
          name="comment"
          value={userComment}
          onChange={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleInputChange(e, setUserComment);
          }}
          onKeyUp={stopFullscreenShortcut}
        />
      </FormLabel>
      <FormButton type="submit">Submit</FormButton>
    </Form>
  );
  if (loading || showLoading) {
    commentBox = (
      <TransitionStateWrapper>
        <Loader
          type="TailSpin"
          color="#dd8a5f"
          radius={5}
          style={{ textAlign: 'center' }}
          height={80}
          width={80}
        />
      </TransitionStateWrapper>
    );
  }
  if (showSuccess && data) {
    commentBox = (
      <TransitionStateWrapper>
        <Checkmark size={80} color="#dd8a5f" />
        <SuccessMessage>
          <SuccessMessagePrimary>Message Sent!</SuccessMessagePrimary>
          <SuccessMessageSecondary>
            Comments are reviewed daily
          </SuccessMessageSecondary>
        </SuccessMessage>
      </TransitionStateWrapper>
    );
  }
  if (showError && error) {
    commentBox = (
      <ErrorMessage dangerouslySetInnerHTML={{ __html: error.message }} />
    );
  }
  return (
    <FormWrapper>
      <FormHeading>New Comment:</FormHeading>
      <FormBodyWrapper>{commentBox}</FormBodyWrapper>
    </FormWrapper>
  );
};

export default CommentForm;
